import { useCallback, useMemo } from "react";
import { useSearchParams } from "react-router-dom";

/**
 * Sincroniza un valor con un query param de la URL. Pattern "URL es la
 * fuente de verdad" — compartir un link lleva el estado de filtros,
 * paginación y search a quien lo abra.
 *
 * - `get` tipado: devuelve el string del param o el fallback.
 * - `set` tipado: setea / limpia; valores vacíos se eliminan de la URL.
 * - Patch múltiple en una sola operación evita renders dobles.
 *
 * Ejemplo:
 *   const { get, set } = useQueryState();
 *   const search = get("q", "");
 *   set({ q: next, offset: "0" });  // vuelve a página 1 al buscar.
 */
export function useQueryState() {
  const [params, setParams] = useSearchParams();

  const get = useCallback((key: string, fallback = "") => params.get(key) ?? fallback, [params]);

  const set = useCallback(
    (patch: Record<string, string | number | null | undefined>) => {
      setParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          for (const [k, v] of Object.entries(patch)) {
            if (v === null || v === undefined || v === "") {
              next.delete(k);
            } else {
              next.set(k, String(v));
            }
          }
          return next;
        },
        { replace: true },
      );
    },
    [setParams],
  );

  const all = useMemo(() => Object.fromEntries(params.entries()), [params]);

  return { get, set, all };
}

/**
 * Variante con tipo numérico — útil para offsets, limits, páginas.
 * Devuelve `fallback` si el param no existe o no parsea.
 */
export function useNumberQueryParam(key: string, fallback: number): number {
  const { get } = useQueryState();
  const raw = get(key, "");
  const parsed = Number(raw);
  return Number.isFinite(parsed) && raw.trim() !== "" ? parsed : fallback;
}
